/**
 * Notification Service
 * 
 * Frontend API service for notification endpoints.
 * Uses the same pattern as groupService.js
 */

import { makeApiCall, apiGet, apiPost, apiPut, apiDelete } from '@/lib/apiUtils';

// Use admin or member endpoint based on user role
const getBaseUrl = (role) => {
  return role === 'admin' ? '/api/admin/notifications' : '/api/website-user/notifications';
};

/**
 * Get notifications with pagination and filters
 */
export const getNotifications = async ({ page = 1, limit = 20, type, isRead, search, role = 'admin' } = {}) => {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('limit', limit);
  if (type && type !== 'all') params.append('type', type);
  if (isRead !== undefined && isRead !== 'all') params.append('isRead', isRead);
  if (search) params.append('search', search);

  return apiGet(`${getBaseUrl(role)}?${params.toString()}`);
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (role = 'admin') => {
  return apiGet(`${getBaseUrl(role)}/unread-count`);
};

/**
 * Get notification types
 */
export const getNotificationTypes = async (role = 'admin') => {
  return apiGet(`${getBaseUrl(role)}/types`);
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (id, role = 'admin') => {
  return apiPut(`${getBaseUrl(role)}/${id}/read`);
};

/**
 * Mark all notifications as read
 */
export const markAllNotificationsAsRead = async (role = 'admin') => {
  return apiPut(`${getBaseUrl(role)}/read-all`);
};

/**
 * Delete a notification
 */
export const deleteNotification = async (id, role = 'admin') => {
  return apiDelete(`${getBaseUrl(role)}/${id}`);
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = async (role = 'admin') => {
  return apiDelete(`${getBaseUrl(role)}/clear-all`);
};

/**
 * Send notification (admin only)
 */
export const sendNotification = async ({ usernames, type, title, body, clickAction, metadata, sendToAll }) => {
  return apiPost('/api/admin/notifications/send', {
    usernames,
    type,
    title,
    body,
    clickAction,
    metadata,
    sendToAll,
  });
};
